import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  OnChanges,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { Subscription, interval } from 'rxjs';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  UntypedFormArray,
  UntypedFormControl,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { CustomValidators } from '@app/core/utils/custom-validators';
import { Role, User } from '@app/core/models/user';
import { Client } from '@app/core/models/client';
import { UserService } from '@app/core/services/user.service';
import { ClientService } from '@app/core/services/client.service';
import { SessionService } from '@app/security/session.service';
import { Ordering } from '@app/core/utils/ordering';
import { MessageDialogComponent } from '@app/shared/message-dialog/message-dialog.component';
import {
  Permissions,
  PermissionService,
} from '@app/core/services/permission.service';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ControlTagsService,
  ControlGroup,
} from '@app/core/services/control-tags.service';
import { TwoWaySharedService } from '@app/core/services/two-way-shared.service';
import { FeatureFlags } from '@app/core/models/feature-flags';
import { FeatureService } from '@app/core/services/feature.service';
import * as _ from 'lodash';
import { SpinnerComponent } from '../../shared/spinner/spinner.component';
import { ClientHeaderComponent } from '../../shared/client-header/client-header.component';
import { TextInputComponent } from '../../shared/text-input/text-input.component';
import { InputInfoBoxComponent } from '../../shared/input-info-box/input-info-box.component';
import { NgClass } from '@angular/common';
import { ErrorDisplayComponent } from '../../shared/error-display/error-display.component';
import { UserResetComponent } from '../user-reset/user-reset.component';
import { MessageDialogComponent as MessageDialogComponent_1 } from '../../shared/message-dialog/message-dialog.component';
import { InputFieldPipe } from '../../shared/pipes/input-field.pipe';
import { ToggleSwitchComponent } from '../../shared/toggle-switch/toggle-switch.component';
import { QuicksightConsoleRole } from '@app/data-and-reporting/services/quicksight-dashboard.service';
import {
  QuicksightAdminService,
  QuicksightRegisterReaderResponse,
} from '@app/core/services/quicksight-admin.service';

@Component({
  selector: 'app-user-edit',
  templateUrl: './user-edit.component.html',
  styleUrls: ['./user-edit.component.scss'],
  standalone: true,
  imports: [
    SpinnerComponent,
    ClientHeaderComponent,
    TextInputComponent,
    InputInfoBoxComponent,
    FormsModule,
    ReactiveFormsModule,
    NgClass,
    ErrorDisplayComponent,
    UserResetComponent,
    MessageDialogComponent_1,
    InputFieldPipe,
    ToggleSwitchComponent,
  ],
})
export class UserEditComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild(MessageDialogComponent, { static: true }) messageDialog;
  // Both MFA dialogs sit inside the template's `@if (!isLoading)` block, so a
  // static query never resolved them and showMessage() threw on both the
  // success and failure paths — these have to be dynamic.
  //
  // Not gated on auth0-login-redirect, unlike the rest of DV-26613: the legacy
  // reset raised the same TypeError instead of reporting its own outcome, and
  // re-breaking it for flag parity would be restoring a crash, not behaviour.
  @ViewChild('resetMfaSucess') resetMfaSucess: MessageDialogComponent;
  @ViewChild('resetMfaFail') resetMfaFail: MessageDialogComponent;
  // Dynamic for the same reason as the MFA dialogs above: both sit inside the
  // template's `@if (!isLoading)` block.
  @ViewChild('resetLoginAttemptsSuccess')
  resetLoginAttemptsSuccess: MessageDialogComponent;
  @ViewChild('resetLoginAttemptsFail')
  resetLoginAttemptsFail: MessageDialogComponent;

  @Input() userID: string;
  @Output() close: EventEmitter<string> = new EventEmitter();
  @Input() isNew: boolean;
  loading: boolean;
  showUserInfo: boolean = true;
  showRoleInfo: boolean = true;
  showClientInfo: boolean = true;
  showReset: boolean = false;
  showQuicksightInfo: boolean = true;
  quicksightRole: QuicksightConsoleRole | null = null;
  quicksightStatusLoading: boolean = false;
  quicksightRegistering: boolean = false;
  quicksightProvisioning: boolean = false;
  private quicksightProvisioningPollAttempts: number = 0;
  private quicksightProvisioningPollSubscription: Subscription | null = null;
  adminRoleOptions: Role[] = [];
  twoWayRoleOptions: Role[] = [];
  selectedSecondaryClient: Client;
  userInfoValidGroup: UntypedFormGroup;
  user: User = new User();
  availableClients: Client[] = [];
  availableSecondaryClients: Client[] = [];
  allClients: Client[] = [];
  showAll: boolean;
  permissions = Permissions;
  twoWayPermRegex = /two_way/;
  controlGroups: ControlGroup[] = [];
  currentClient: Client;
  featureFlags = FeatureFlags;

  constructor(
    private userService: UserService,
    private clientService: ClientService,
    private formBuilder: UntypedFormBuilder,
    private sessionService: SessionService,
    public ps: PermissionService,
    private controlTagsService: ControlTagsService,
    private featureService: FeatureService,
    public twoWayService: TwoWaySharedService,
    private quicksightAdminService: QuicksightAdminService,
  ) {}

  ngOnInit() {
    this.getRoles();
    this.getClients();
    this.currentClient = this.sessionService.getCurrentUsersClient();
    if (!this.isNew) {
      this.loading = true;
    }
  }

  ngOnChanges(changes) {
    if (changes.userID && !changes.userID.firstChange) {
      this.resetUser();
    }
  }

  ngOnDestroy(): void {
    this.stopQuicksightProvisioningPoll();
  }

  initForm(user) {
    this.userInfoValidGroup = this.formBuilder.group({
      first_name: [user.first_name, Validators.required],
      last_name: [user.last_name, Validators.required],
      group_id: [user.group_id],
      primary_client: [user.client_id, Validators.required],
      secondary_clients: new UntypedFormArray([]),
      email: [
        user.email_address,
        [Validators.required, CustomValidators.emailToBeTrimmed],
      ],
      // roles get updated by other unnecessarily convoluted means... They come from a key 'role_id'
      //  which is actually an array value w/ both admin & two way `['admin_role', 'two_way_role']`
      admin_role: [null, Validators.required],
      two_way_role: [null],
    });
    // better to disable here than in template, according to Angular
    this.userInfoValidGroup.get('primary_client').disable();

    _.each(user.secondary_clients, (secondaryClient) => {
      this.selectedSecondaryClientsCtl.push(
        new UntypedFormControl(secondaryClient),
      );
    });

    this.userInfoValidGroup.controls['primary_client'].valueChanges.subscribe(
      () => {
        this.handlePrimaryClientChange();
      },
    );
  }
  get hideControlGroup(): boolean {
    return this.featureService.checkFlag(this.featureFlags.hide_control_group);
  }
  get selectedSecondaryClients(): string[] {
    return this.selectedSecondaryClientsCtl.value;
  }

  get selectedSecondaryClientsCtl(): UntypedFormArray {
    return this.userInfoValidGroup.controls[
      'secondary_clients'
    ] as UntypedFormArray;
  }

  resetPasswordConfig() {
    return {
      title: 'Send password link',
      icon: 'icon-secure',
      msg: `An auth link has been generated for the user ${this.user.email_address}. The user will receive an email with the link to set their password.`,
      btns: [{ name: 'Close', icon: 'fa-times' }],
    };
  }

  getClients() {
    const ordering = new Ordering('data.company_name');

    this.clientService.getAllClients(ordering).subscribe(
      (clients) => {
        this.allClients = clients;
        this.resetUser();
      },
      (error) => {
        this.loading = false;
      },
    );
  }

  resetUser() {
    if (!this.isNew) {
      this.userService.getUserById(this.userID).subscribe((user) => {
        this.initForm(user);
        this.user = user;
        this.fetchQuicksightStatus();

        const twoWayPermRegex = this.twoWayPermRegex; // b/c the `this` context changes in lodash callbacks

        this.selectedAdminRole.setValue(
          _.reject(user.role_id, (role) => {
            return twoWayPermRegex.test(role);
          })[0],
        );

        this.selectedTwoWayRole.setValue(
          _.filter(user.role_id, (role) => {
            return twoWayPermRegex.test(role);
          })[0],
        );

        if (
          this.selectedSecondaryClients.length &&
          this.selectedSecondaryClients[0] === '*'
        ) {
          this.availableClients = [];
        } else {
          this.updateAvailableClients();
        }

        this.userInfoValidGroup.get('group_id').setValue(user.group_id); // set group_id to user's value in form
        this.userInfoValidGroup.get('primary_client').setValue(user.client_id); // set initial value for primary client id in form
        this.refreshControlGroups();

        this.loading = false;
      });
    } else {
      this.user.client_id = this.sessionService.currentUser.client.id;
      this.initForm(this.user);
      this.userInfoValidGroup
        .get('primary_client')
        .setValue(this.user.client_id); // set initial value for primary client id in form
      this.updateAvailableClients();
      this.refreshControlGroups();
      this.loading = false;
    }
  }

  handlePrimaryClientChange() {
    if (
      this.selectedSecondaryClient &&
      this.selectedSecondaryClient.id ===
        this.userInfoValidGroup.controls['primary_client'].value
    ) {
      this.selectedSecondaryClient = null;
    }
    this.updateAvailableClients();
    this.refreshControlGroups();
  }

  // filters this.allClients to get options for the Primary Client dropdown
  private newAvailableClients(): Client[] {
    return _.reject(this.allClients, (client) => {
      return this.isInSelectedSecondaryClientArray(client);
    });
  }

  // filters this.allClients to get options for the Secondary Client dropdown
  private newAvailableSecondaryClients(): Client[] {
    return _.reject(this.allClients, (client) => {
      return (
        this.isInSelectedSecondaryClientArray(client) ||
        this.isPrimaryClient(client)
      );
    });
  }

  private isInSelectedSecondaryClientArray(client): boolean {
    return _.includes(this.selectedSecondaryClientsCtl.value, client.id);
  }

  private isPrimaryClient(client): boolean {
    return (
      this.userInfoValidGroup.controls['primary_client'].value === client.id
    );
  }

  private updateAvailableClients(): void {
    this.availableClients = this.newAvailableClients();
    if (
      this.selectedSecondaryClients.length &&
      this.selectedSecondaryClients[0] === '*'
    ) {
      this.availableSecondaryClients = [];
    } else {
      this.availableSecondaryClients = this.newAvailableSecondaryClients();
    }
  }

  canRemove(clientId): boolean {
    const client = this.allClients.find((c) => c.id === clientId);
    return !!client;
  }

  getCompanyNameById(clientId) {
    const client = this.allClients.find((c) => c.id === clientId);
    if (client) {
      return client.company_name;
    } else {
      if (clientId === '*') {
        return 'You have all the clients';
      }
      return clientId;
    }
  }

  adminRoleChange() {
    if (this.selectedAdminRole.value === 'relay-super-admin') {
      this.userInfoValidGroup.controls['secondary_clients'] =
        new UntypedFormArray([new UntypedFormControl('*')]);
    } else {
      if (
        this.selectedSecondaryClients.length &&
        this.selectedSecondaryClients[0] === '*'
      ) {
        // TODO: change to this after ng8 upgrade:
        // this.selectedSecondaryClientsCtl.clear();
        this.userInfoValidGroup.controls['secondary_clients'] =
          new UntypedFormArray([]);
      }
    }
    this.updateAvailableClients();
    this.roleChange();
  }

  roleChange() {
    const newRoles = _.compact([
      this.selectedAdminRole.value,
      this.selectedTwoWayRole.value,
    ]);
    this.user.role_id = newRoles; // manually set model to new value
  }

  get selectedAdminRole(): AbstractControl {
    return this.userInfoValidGroup.get('admin_role') as AbstractControl;
  }

  get selectedTwoWayRole(): AbstractControl {
    return this.userInfoValidGroup.get('two_way_role') as AbstractControl;
  }

  get selectedControlGroup(): AbstractControl {
    return this.userInfoValidGroup.get('group_id') as AbstractControl;
  }

  get selectedControlGroupId(): string {
    return this.selectedControlGroup.value;
  }

  addClient() {
    if (this.selectedSecondaryClient) {
      this.selectedSecondaryClientsCtl.insert(
        0,
        new UntypedFormControl(this.selectedSecondaryClient.id),
      );
      this.updateAvailableClients();
      this.selectedSecondaryClient = null;
    }
  }

  removeClient(clientId: string, index: number) {
    const clientToRemoveIndex =
      this.selectedSecondaryClientsCtl.value.indexOf(clientId);
    this.selectedSecondaryClientsCtl.removeAt(clientToRemoveIndex);
    this.updateAvailableClients();
  }

  /**
   * Extract and store user data from the form in the DOM
   *
   * @returns the user data
   */
  getUpdatedUserModel(): User {
    const formData = this.userInfoValidGroup.getRawValue();
    let updatedUserData = {};
    if (this.isNew) {
      updatedUserData = _.assign(this.user, {
        firstName: formData.first_name,
        lastName: formData.last_name,
        emailAddress: formData.email,
        email_address: formData.email,
        clientId: formData.primary_client,
        secondaryClients: formData.secondary_clients,
        roleId: [formData.admin_role, formData.two_way_role].filter(Boolean),
      });
      // only add group_id key if a two way role is present
      if (formData.two_way_role) {
        this.user.groupId = formData.group_id;
      }
    } else {
      updatedUserData = _.assign(this.user, {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email_address: formData.email,
        client_id: formData.primary_client,
        secondary_clients: formData.secondary_clients,
        role_id: [formData.admin_role, formData.two_way_role].filter(Boolean),
      });
      // only add group_id key if a two way role is present
      if (formData.two_way_role) {
        // Should absolutely refactor to make better use of reactive forms
        this.user.group_id = formData.group_id;
      }
    }

    return updatedUserData as User;
  }

  onSave() {
    this.user = this.getUpdatedUserModel();

    for (const control in this.userInfoValidGroup.controls) {
      if (
        !this.userInfoValidGroup.controls.hasOwnProperty(control) &&
        !this.userInfoValidGroup.controls[control].value
      ) {
        continue;
      }
      this.userInfoValidGroup.controls[control].markAsTouched();
    }

    if (this.userInfoValidGroup.valid) {
      if (this.isNew) {
        this.createUser();
      } else {
        this.updateUser();
      }
    }
  }

  createUser() {
    const userToSend = Object.assign({}, this.user);
    delete userToSend.selected;
    this.userService.createUser(userToSend).subscribe({
      next: (resp) => {
        resp.sendUserEmail ? (this.showReset = true) : this.handleClose();
      },
      error: (error) => {
        if (error?.error?.reason ?? error?.error?.message) {
          this.messageDialog.showMessage(
            error?.error?.reason ?? error?.error?.message,
          );
        }
      },
    });
  }

  updateUser() {
    if (this.availableClients.length === 0) {
      this.selectedSecondaryClientsCtl.setValue(['*']);
    }
    this.userService.updateUser(this.user).subscribe(
      (d) => {
        this.handleClose();
      },
      (error: HttpErrorResponse) => {
        let reason;
        if (error && error.error && error.error.reason) {
          reason = error.error.reason;
        }
        this.messageDialog.showMessage(reason);
      },
    );
  }

  getRoles(): void {
    this.userService.getRoles().subscribe((roles) => {
      this.adminRoleOptions = _.reject(roles, (role) => {
        return this.twoWayPermRegex.test(role.id);
      });

      this.twoWayRoleOptions = _.filter(roles, (role) => {
        return this.twoWayPermRegex.test(role.id);
      });
    });
  }

  handleClose(): void {
    this.showReset = false;
    this.close.emit(this.userID);
  }

  /**
   * Sends the user a link to set a new password. The endpoint picks the source
   * — Auth0 when `auth0-login-redirect` is on for that user's client, the
   * legacy mailer when it isn't — so there's nothing to branch on for *which
   * kind* of reset happens.
   *
   * Which client scopes the request does differ, though. Auth0 wants the
   * *target* user's client and rejects a mismatch; the legacy path was only
   * ever called with the acting admin's own client, so that is what it keeps
   * getting — sending the target's instead would change a request that works
   * today for a client not yet on Auth0.
   */
  resetPassword() {
    const clientId = this.isAuth0LoginRedirectEnabled
      ? this.user.client_id
      : this.sessionService.currentUser.clientId;
    this.sessionService
      .resetPasswordAdmin(this.user.email_address, clientId)
      .subscribe((f) => {
        this.showReset = true;
      });
  }

  /**
   * Clears the user's MFA enrollment so they are prompted to enrol again on
   * their next login — for a lost or stolen device.
   *
   * On Auth0 the client-scoped endpoint decides whether that means clearing
   * Auth0 enrollments or the legacy stored secret, so there is nothing to
   * branch on per target user — branching on *which factors to clear* here
   * would use the acting admin's flag value to answer a question about the
   * target user, which is only right by coincidence.
   *
   * Off the flag this falls back to the pre-DV-26613 unscoped
   * `POST /reset-mfa`, the only MFA reset a client on the legacy login path
   * has.
   */
  resetMFASecret() {
    const reset$ = this.isAuth0LoginRedirectEnabled
      ? this.sessionService.resetMFAEnrollments(
          this.user.email_address,
          this.user.client_id,
        )
      : this.sessionService.resetMFASecret(this.user.email_address);

    reset$.subscribe(
      (resp) => {
        this.resetMfaSucess.showMessage();
      },
      (error) => {
        this.resetMfaFail.showMessage();
      },
    );
  }

  /**
   * Clears the Auth0 brute-force blocks holding the user out after too many
   * failed logins, so they can sign in again immediately.
   *
   * Unlike the password and MFA resets there is no legacy equivalent — the
   * endpoint refuses a client still on the legacy login path rather than
   * clearing its Redis attempt counter, which is why this one is flag-gated
   * where those two are not (see isLoginAttemptsResetAllowed).
   */
  resetLoginAttempts() {
    this.sessionService
      .resetLoginAttempts(this.user.email_address, this.user.client_id)
      .subscribe(
        (resp) => {
          this.resetLoginAttemptsSuccess.showMessage();
        },
        (error) => {
          this.resetLoginAttemptsFail.showMessage();
        },
      );
  }

  fetchQuicksightStatus(): void {
    if (!this.isQuicksightRegistrationToggleEnabled) {
      return;
    }
    this.quicksightStatusLoading = true;
    this.quicksightAdminService
      .getRegistrationStatus(this.user.client_id, this.user.email_address)
      .subscribe({
        next: (response) => {
          this.quicksightRole = response.role;
          this.quicksightStatusLoading = false;
        },
        error: () => {
          this.quicksightStatusLoading = false;
        },
      });
  }

  onQuicksightToggle(): void {
    if (this.isQuicksightToggleDisabled) {
      return;
    }
    this.quicksightRegistering = true;
    this.registerQuicksightReader();
  }

  private registerQuicksightReader(): void {
    this.quicksightAdminService
      .registerReader(this.user.client_id, this.user.email_address)
      .subscribe({
        next: (response) => this.handleQuicksightRegisterResponse(response),
        error: (error: HttpErrorResponse) =>
          this.handleQuicksightRegisterError(error),
      });
  }

  private handleQuicksightRegisterResponse(
    response: QuicksightRegisterReaderResponse,
  ): void {
    if (response.status === 'registered') {
      this.stopQuicksightProvisioningPoll();
      this.quicksightRole = response.role;
      this.quicksightRegistering = false;
      this.quicksightProvisioning = false;
      return;
    }

    // status === 'provisioning': the client's QuickSight namespace wasn't ready yet, so the
    // backend kicked off (or joined) background auto-provisioning and returned immediately.
    // There's no separate status-check endpoint -- retrying this exact request is what
    // completes registration once the namespace is ready, so we poll it ourselves.
    this.quicksightRegistering = false;
    this.quicksightProvisioning = true;
    this.startQuicksightProvisioningPoll();
  }

  private handleQuicksightRegisterError(error: HttpErrorResponse): void {
    this.stopQuicksightProvisioningPoll();
    this.quicksightRegistering = false;
    this.quicksightProvisioning = false;
    this.messageDialog.showMessage(
      error?.error?.message ??
        'Failed to register Relay Insights Studio reader user.',
    );
  }

  // Polls the register endpoint every 5s -- namespace auto-provisioning can take up to ~60s,
  // and retrying this same request is the only way to observe completion (see
  // handleQuicksightRegisterResponse). Gives up after ~90s (18 attempts) rather than polling
  // forever, so a stuck provisioning attempt surfaces as an actionable error instead of an
  // indefinitely-disabled toggle.
  private startQuicksightProvisioningPoll(): void {
    if (this.quicksightProvisioningPollSubscription) {
      return;
    }
    this.quicksightProvisioningPollAttempts = 0;
    this.quicksightProvisioningPollSubscription = interval(5000).subscribe(
      () => {
        this.quicksightProvisioningPollAttempts += 1;
        if (this.quicksightProvisioningPollAttempts > 18) {
          this.stopQuicksightProvisioningPoll();
          this.quicksightProvisioning = false;
          this.messageDialog.showMessage(
            'Relay Insights Studio is still provisioning. Please try again in a moment.',
          );
          return;
        }
        this.registerQuicksightReader();
      },
    );
  }

  private stopQuicksightProvisioningPoll(): void {
    if (this.quicksightProvisioningPollSubscription) {
      this.quicksightProvisioningPollSubscription.unsubscribe();
      this.quicksightProvisioningPollSubscription = null;
    }
  }

  // Gated behind the `quicksight-register-toggle` LaunchDarkly flag (rather than hardcoded to
  // an environment) so rollout can be targeted per client/environment without a deploy.
  get isQuicksightRegistrationToggleEnabled(): boolean {
    return this.featureService.checkFlag(
      this.featureFlags.quicksight_register_toggle,
    );
  }

  // Relay-internal admins only — narrower than isMFAResetAllowed, which also
  // allows client_admin. The ticket's requirement is simply "only Relay admins
  // can click".
  get isQuicksightAdmin(): boolean {
    return (
      this.sessionService.currentUser.role_id.includes('relay-super-admin') ||
      this.sessionService.currentUser.role_id.includes('relay_admin')
    );
  }

  // AUTHOR is treated the same as READER for toggle behavior: already registered, no downgrade
  // path exists in this UI. The displayed status text still shows the real role separately.
  get isQuicksightRegistered(): boolean {
    return this.quicksightRole === 'READER' || this.quicksightRole === 'AUTHOR';
  }

  get isQuicksightToggleDisabled(): boolean {
    return (
      !this.isQuicksightAdmin ||
      this.isQuicksightRegistered ||
      this.quicksightStatusLoading ||
      this.quicksightRegistering ||
      this.quicksightProvisioning
    );
  }

  // Explains why the toggle is locked whenever it looks actionable but isn't. Registered takes
  // precedence over the admin check since it applies to admins too — there's no downgrade path
  // in this UI, so the message is about the add-only nature of the action, not permissions.
  get quicksightToggleTooltip(): string | undefined {
    if (this.isQuicksightRegistered) {
      return "This user's Relay Insights Studio registration status cannot be modified.";
    }
    if (this.quicksightProvisioning) {
      return 'Relay Insights Studio access is still provisioning for this client.';
    }
    if (!this.isQuicksightAdmin) {
      return 'Relay Insights Studio users can only be registered by Relay Admins';
    }
    return undefined;
  }

  /**
   * Whether this screen takes the Auth0 credential-reset path. Every
   * DV-26613/DIS-1749 change to it hangs off this one getter — the widened MFA
   * reset gate, the client-scoped reset endpoints, and the Reset Login Attempts
   * button — so that with the flag off the screen looks and behaves exactly as
   * it did before the Auth0 work.
   *
   * Flag *absent* is not flag *off* (DIS-1749). auth0-login-redirect drives the
   * backend rollout and is read there through the server-side SDK; it is the
   * only flag this frontend reads that the backend also reads, and
   * LaunchDarkly withholds a flag from `allFlags()` unless it is published to
   * client-side IDs, so the browser's set omits it however it is targeted.
   * Failing closed on absence would put the whole screen back on the legacy
   * path while the backend serves Auth0 — which is what shipped first, and it
   * hid the login-attempts button with the flag fully on for the environment.
   * An explicit `false` (flag published to client-side IDs and turned off) does
   * take the legacy path. Publish the flag and this becomes a true per-flag
   * gate on its own, with no code change.
   *
   * FeatureService evaluates against the *acting admin* as a user-kind context,
   * so this is not a per-target-user answer and mustn't be read as one. The
   * endpoints re-evaluate the flag themselves with the correct multi-context
   * and stay the boundary.
   */
  get isAuth0LoginRedirectEnabled(): boolean {
    return this.featureService.checkFlag(
      this.featureFlags.auth0_login_redirect,
      true,
    );
  }

  // The acting-user roles the Auth0 credential-reset endpoints accept. Only the
  // acting user's role matters — any user in the client can have their MFA
  // reset or their login attempts cleared, including non-admins. Those
  // endpoints stay the security boundary and additionally check that the target
  // user belongs to the requested client.
  //
  // Deliberately no client check here, for any of the roles. The endpoints let
  // relay_admin and relay-super-admin reset for a user in any client, and
  // confine client_admin to the clients it holds — but a client admin can only
  // ever reach users of those clients anyway, because the user search this
  // screen is opened from filters on the target user's primary client and is
  // itself behind the client-authorization hook. So a client gate on the button
  // would only ever hide something already unreachable.
  get isCredentialResetRoleAllowed(): boolean {
    const roleIds = this.sessionService.currentUser?.role_id ?? [];
    return ['relay-super-admin', 'relay_admin', 'client_admin'].some((roleId) =>
      roleIds.includes(roleId),
    );
  }

  get isMFAResetAllowed(): boolean {
    // Off the flag, the pre-DV-26613 gate verbatim: the *target* user's role
    // decides for a relay-super-admin, and a client_admin never sees the
    // button. Widening it only makes sense alongside the Auth0 endpoint that
    // enforces the wider set, so a client on the legacy login path keeps
    // exactly the button visibility it had.
    if (!this.isAuth0LoginRedirectEnabled) {
      return (
        ((this.user.role_id.includes('relay-super-admin') ||
          this.user.role_id.includes('relay_admin')) &&
          this.sessionService.currentUser.role_id.includes(
            'relay-super-admin',
          )) ||
        this.sessionService.currentUser.role_id.includes('relay_admin')
      );
    }
    return this.isCredentialResetRoleAllowed;
  }

  /**
   * Same acting-user roles as the Auth0 MFA reset, and the same endpoint-side
   * enforcement — but flag-gated where the password and MFA resets only switch
   * *which* endpoint they call.
   *
   * This reset exists for Auth0 only: the endpoint refuses a client still on
   * the legacy login path rather than clearing its Redis attempt counter, and
   * there is no legacy equivalent to fall back to. So off the flag the button
   * is absent rather than legacy-flavoured, which is also what parity with
   * pre-DV-26613 requires — it did not exist then.
   */
  get isLoginAttemptsResetAllowed(): boolean {
    return (
      this.isAuth0LoginRedirectEnabled && this.isCredentialResetRoleAllowed
    );
  }

  get isLoading(): boolean {
    return this.loading || !this.userInfoValidGroup;
  }

  refreshControlGroups(): void {
    if (this.twoWayService.showTwoWay()) {
      const clientId =
        this.userInfoValidGroup.get('primary_client').value ||
        this.sessionService.getCurrentUsersClient()['id'];
      this.controlGroups = [];

      this.controlTagsService.getGroups(clientId).subscribe((newGroups) => {
        this.controlGroups = this.controlGroups.concat(newGroups);
      });
    }
  }

  /**
   * Updates the user's control group every time a value is selected.
   *
   * @param selectedValue when the group selection is changed
   */
  controlGroupChanged(selectedValue: string): void {
    // set control group to null if none are selected
    if (!selectedValue) {
      selectedValue = null;
    }

    this.selectedControlGroup.setValue(selectedValue);
  }
}
